import {useEffect, useRef} from 'react';
import {
  hybridHoldExpiresAt,
  isHybridBookingPending,
  isHybridHoldRunning
} from 'helpers/functionUtils/hybridDelivery';

const POLL_INTERVAL = 10000;
const MAX_WAIT_AFTER_DEADLINE = 10 * 60 * 1000;

// The backend books a while after the window closes, and only then does the
// order carry a delivery, so the refetching has to survive the countdown
// reaching zero instead of stopping there.
const useHybridHoldRefresh = (order, serviceFeatures, refresh) => {
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;

  const expiresAt = hybridHoldExpiresAt(order);
  const isWatching =
    isHybridHoldRunning(order, serviceFeatures) ||
    isHybridBookingPending(order, serviceFeatures);

  useEffect(() => {
    if (!isWatching || !expiresAt) return undefined;

    const deadline = new Date(expiresAt).getTime();
    if (Date.now() > deadline + MAX_WAIT_AFTER_DEADLINE) return undefined;

    let interval;
    const start = () => {
      refreshRef.current();
      interval = setInterval(() => {
        if (Date.now() > deadline + MAX_WAIT_AFTER_DEADLINE) {
          clearInterval(interval);
          return;
        }
        refreshRef.current();
      }, POLL_INTERVAL);
    };

    const timeout = setTimeout(
      start,
      Math.max(0, deadline - Date.now()) + 1000
    );

    return () => {
      clearTimeout(timeout);
      if (interval) clearInterval(interval);
    };
  }, [isWatching, expiresAt]);
};

export default useHybridHoldRefresh;
